import React, { useState, useEffect } from "react";
import { PlusIcon, TrashIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import {
  useOutstandingDocuments,
  useSaveOutstandingDocuments,
} from "http-hooks/opnbal";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionButton,
  Badge,
} from "components/ui/Table";
import Select from "components/ui/Select";
import Input from "components/ui/Input";

// Constants moved to top to prevent re-renders
const docTypeOptions = [
  { value: "OPN", label: "Bill" },
  { value: "OPNDN", label: "Debit Note" },
  { value: "OPNCN", label: "Credit Note" },
];

const emptyDocument = {
  docType: "",
  docNo: "",
  docDate: "",
  amount: "",
  remark: "",
  isAdjusted: false,
};

// Loading skeleton component
const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="flex justify-between items-center mb-6">
      <div>
        <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-64"></div>
      </div>
      <div className="h-12 bg-gray-200 rounded w-32"></div>
    </div>
    <div className="space-y-4">
      <div className="h-10 bg-gray-200 rounded w-full"></div>
      <div className="h-32 bg-gray-200 rounded w-full"></div>
    </div>
  </div>
);

const OutstandingDocumentsModal = ({
  vendorId,
  customerId,
  vendorName,
  customerName,
  onClose,
}) => {
  // Consolidated state - documents with calculated total
  const [documentsState, setDocumentsState] = useState({
    documents: [],
    total: 0,
  });

  const {
    data: existingDocuments,
    isLoading: isLoadingDocs,
    error: docsError,
  } = useOutstandingDocuments(vendorId, customerId, true);

  const { mutate: saveDocuments, isPending: isSaving } =
    useSaveOutstandingDocuments({
      onSuccess: () => {
        onClose();
      },
    });

  // Initialize documents when existing documents load
  useEffect(() => {
    if (existingDocuments) {
      const documents =
        existingDocuments.length > 0
          ? existingDocuments
          : [{ ...emptyDocument }];
      const total = calculateTotal(documents);
      setDocumentsState({ documents, total });
    }
  }, [existingDocuments]);

  // Utility function to calculate total (only non-adjusted documents)
  const calculateTotal = (documents) => {
    return documents.reduce((sum, doc) => {
      // Only include non-adjusted documents in calculation
      if (doc.isAdjusted) return sum;
      const amount = parseFloat(doc.amount) || 0;
      return sum + (doc.docType === "OPNCN" ? -amount : amount);
    }, 0);
  };

  const handleDocumentChange = (index, field, value) => {
    setDocumentsState((prev) => {
      const newDocuments = [...prev.documents];
      newDocuments[index] = { ...newDocuments[index], [field]: value };
      const total = calculateTotal(newDocuments);
      return { documents: newDocuments, total };
    });
  };

  const handleAddRow = () => {
    setDocumentsState((prev) => {
      const newDocuments = [...prev.documents, { ...emptyDocument }];
      const total = calculateTotal(newDocuments);
      return { documents: newDocuments, total };
    });
  };

  const handleRemoveRow = (index) => {
    setDocumentsState((prev) => {
      const newDocuments = prev.documents.filter((_, i) => i !== index);
      const total = calculateTotal(newDocuments);
      return { documents: newDocuments, total };
    });
  };

  const handleSave = () => {
    // Only process non-adjusted documents (following PHP logic)
    const validDocuments = documentsState.documents.filter(
      (doc) => !doc.isAdjusted && doc.docType && doc.docNo && doc.docDate && doc.amount
    );

    if (validDocuments.length === 0) {
      return;
    }

    const payload = {
      vendorId,
      customerId,
      documents: validDocuments,
    };

    saveDocuments(payload);
  };

  if (isLoadingDocs) {
    return <FormSkeleton />;
  }

  return (
    <form>
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Outstanding Documents
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage outstanding documents for {vendorName} → {customerName}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <p className="text-lg font-bold text-blue-800">
              Total: ₹{documentsState.total.toFixed(2)}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {documentsState.total >= 0 
                ? `${customerName} → will pay to ${vendorName}` 
                : `${vendorName} → will pay to ${customerName}`}
            </p>
            {(() => {
              const adjustedCount = documentsState.documents.filter(doc => doc.isAdjusted).length;
              const nonAdjustedCount = documentsState.documents.filter(doc => !doc.isAdjusted).length;
              if (adjustedCount > 0) {
                return (
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    {adjustedCount} adjusted document{adjustedCount > 1 ? 's' : ''} excluded from total
                  </p>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 overflow-visible">
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH first>Doc Type & Status</TH>
                <TH>Doc No.</TH>
                <TH>Date</TH>
                <TH>Amount</TH>
                <TH>Remarks</TH>
                <TH last>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {documentsState.documents.map((doc, index) => (
                <TR 
                  key={index} 
                  className={`h-16 ${doc.isAdjusted ? 'bg-gray-50 opacity-75' : ''}`}
                >
                  <TD first className="py-4">
                    <div className="flex items-center gap-2">
                      <Select
                        value={
                          docTypeOptions.find(
                            (opt) => opt.value === doc.docType
                          ) || null
                        }
                        onChange={(option) =>
                          handleDocumentChange(
                            index,
                            "docType",
                            option?.value || ""
                          )
                        }
                        options={docTypeOptions}
                        placeholder="Select type"
                        isRequired
                        showLabel={false}
                        isDisabled={doc.isAdjusted}
                      />
                      {doc.isAdjusted && (
                        <Badge variant="warning" className="text-xs">
                          <LockClosedIcon className="h-3 w-3 mr-1" />
                          Adjusted
                        </Badge>
                      )}
                    </div>
                  </TD>
                  <TD className="py-4">
                    <Input
                      type="text"
                      value={doc.docNo}
                      onChange={(e) =>
                        handleDocumentChange(index, "docNo", e.target.value)
                      }
                      placeholder="Document number"
                      isRequired
                      showLabel={false}
                      maxLength={20}
                      disabled={doc.isAdjusted}
                    />
                  </TD>
                  <TD className="py-4">
                    <Input
                      type="date"
                      value={doc.docDate}
                      onChange={(e) =>
                        handleDocumentChange(index, "docDate", e.target.value)
                      }
                      isRequired
                      showLabel={false}
                      disabled={doc.isAdjusted}
                    />
                  </TD>
                  <TD className="py-4">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={doc.amount}
                      onChange={(e) =>
                        handleDocumentChange(index, "amount", e.target.value)
                      }
                      className="text-right"
                      placeholder="0.00"
                      isRequired
                      showLabel={false}
                      disabled={doc.isAdjusted}
                    />
                  </TD>
                  <TD className="py-4">
                    <Input
                      type="text"
                      value={doc.remark}
                      onChange={(e) =>
                        handleDocumentChange(index, "remark", e.target.value)
                      }
                      placeholder="Remarks"
                      showLabel={false}
                      disabled={doc.isAdjusted}
                    />
                  </TD>
                  <TD last className="py-4">
                    <div className="flex space-x-2">
                      <ActionButton
                        onClick={handleAddRow}
                        variant="primary"
                        title="Add Row"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </ActionButton>
                      {documentsState.documents.length > 1 && !doc.isAdjusted && (
                        <ActionButton
                          onClick={() => handleRemoveRow(index)}
                          variant="danger"
                          title="Remove Row"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </div>

      {/* Footer buttons */}
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={handleSave}
          className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving..." : "Save Documents"}
        </button>
      </div>
    </form>
  );
};

export default OutstandingDocumentsModal;
